import { defineStore } from "pinia";
import axios from "axios";
import router from "@/router"; // Import the router for navigation purposes

export const useUserStore = defineStore("user", {
    state: () => ({
        user: null, // Stores user data
        token: null, // Stores authentication token
        isAuthenticated: false, // Authentication status
        role: null, // Stores the user's role
        error: null, // Stores error messages
    }),

    actions: {
        async login(email, password) {
            this.error = null; // Clear previous errors
            try {
                const response = await axios.post("/api/auth/login", { email, password });
                const { user, token } = response.data;

                this.user = user;
                this.token = token;
                this.role = user.role;
                this.isAuthenticated = true;

                // Store token and timestamp in localStorage/sessionStorage for 4-hour persistence
                safeSetItem("authToken", this.token);
                safeSetItem("authTokenTimestamp", Date.now().toString());

                // Only navigate if this is not the first login
                if (!user.primeiroLogin) {
                    // Navigate to the appropriate dashboard based on role
                    switch (this.role) {
                        case "admin_prefeitura":
                            router.push("/dashboard/admin-prefeitura");
                            break;
                        case "prefeitura":
                            router.push("/dashboard/prefeitura");
                            break;
                        case "admin_cemiterio":
                        case "cemiterio":
                            router.push("/dashboard/cemiterio");
                            break;
                        case "admin_funeraria":
                        case "funeraria":
                            router.push("/dashboard/funeraria");
                            break;
                        default:
                            router.push("/unauthorized");
                    }
                }
                // If primeiroLogin is true, do not redirect. The login view will detect the flag and show the inline password change form.
            } catch (err) {
                this.error = err.response?.data?.message || "An error occurred during login.";
                this.isAuthenticated = false;
                throw err;
            }
        },
        async changePassword(changePasswordDto) {
            // eslint-disable-next-line no-useless-catch
            try {
                const response = await axios.patch(
                    "/api/users/change-password",
                    changePasswordDto,
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                );
                // Update user data and role based on the response.
                this.user = response.data;
                this.role = response.data.role;
                return response.data;
            } catch (err) {
                throw err;
            }
        },

        logout() {
            // Clear user data and token
            this.user = null;
            this.token = null;
            this.role = null;
            this.isAuthenticated = false;

            // Remove token and timestamp from localStorage/sessionStorage
            safeRemoveItem("authToken");
            safeRemoveItem("authTokenTimestamp");

            // Redirect to login page
            router.push("/");
        },

        checkAuth() {
            // Check if token exists in localStorage/sessionStorage and is not expired
            const token = safeGetItem("authToken");
            const timestamp = safeGetItem("authTokenTimestamp");
            const now = Date.now();
            const FOUR_HOURS = 4 * 60 * 60 * 1000;
            if (token && timestamp && now - parseInt(timestamp, 10) < FOUR_HOURS) {
                this.token = token;
                this.isAuthenticated = true;
            } else {
                // If expired or missing, clear everything
                this.token = null;
                this.isAuthenticated = false;
                safeRemoveItem("authToken");
                safeRemoveItem("authTokenTimestamp");
            }
        },
    },
});

// Helper functions for storage fallback
function safeSetItem(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        try {
            sessionStorage.setItem(key, value);
        } catch (e2) {
            // Both storages failed
        }
    }
}
function safeGetItem(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        try {
            return sessionStorage.getItem(key);
        } catch (e2) {
            return null;
        }
    }
}
function safeRemoveItem(key) {
    try {
        localStorage.removeItem(key);
    } catch (e) {
        try {
            sessionStorage.removeItem(key);
        } catch (e2) {
            // Both storages failed
        }
    }
}
